import type { RxConflictHandler } from "rxdb";
import { deepEqual } from "rxdb/plugins/utils";

/**
 * A per-row counter that increments on every local edit, and the conflict
 * handler that reads it.
 *
 * The problem it solves: a write is announced back to us over the realtime
 * channel a few hundred milliseconds after it is made — long enough that we
 * have usually written the row again in the meantime. The replication
 * protocol reads an announcement as the master's *current* state, so applying
 * our own late echo rolls the row back to a version we have already replaced.
 * The checkbox you just unticked ticks itself again, and the next edit is
 * built on the rolled-back value, making the loss permanent.
 *
 * Filtering echoes by authorship cannot fix this reliably: the announcement
 * can arrive before the confirmation of the very push that caused it (four
 * milliseconds, observed), so at the moment it lands we do not yet know it is
 * ours. Ordering can. The question worth asking is not "who wrote this" but
 * "is this newer than what I have", and that has an answer the moment the
 * document arrives.
 *
 * The counter lives inside `data` rather than in a column of its own, so that
 * neither Postgres nor the RxDB schema has to change. Rows written before it
 * existed have none, which reads as 0.
 */
const VERSION_KEY = "_version";

/**
 * The one call site that decides whether an incoming state overwrites the
 * local document (`downstream.js`, "areStatesExactlyEqual"). RxDB passes a
 * distinct context to each of its four `isEqual` calls precisely so a handler
 * can answer differently at each; every other context keeps the default
 * meaning, so nothing but this decision changes.
 */
const DOWNSTREAM_APPLY = "downstream-check-if-equal-1";

interface VersionedDoc {
  data?: Record<string, unknown>;
}

function documentVersion(doc: unknown): number {
  const raw = (doc as VersionedDoc | undefined)?.data?.[VERSION_KEY];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
}

/** Writes the next version into `docData` in place. */
function setNextVersion(docData: unknown, previous: unknown): void {
  const doc = docData as VersionedDoc;
  if (!doc?.data || typeof doc.data !== "object") return;
  const next = Math.max(documentVersion(doc), documentVersion(previous)) + 1;
  doc.data[VERSION_KEY] = next;
}

/**
 * Attaches the counter to a collection's local write paths. Deliberately not
 * applied to writes arriving from the server: the replication protocol writes
 * straight to the storage instance and never runs collection hooks, so a
 * state pulled from the master keeps whatever version it was given.
 */
export function trackDocumentVersion(collection: {
  preInsert: (fn: (docData: unknown) => void, parallel: boolean) => void;
  preSave: (
    fn: (docData: unknown, previous: unknown) => void,
    p: boolean,
  ) => void;
}): void {
  collection.preInsert((docData) => setNextVersion(docData, undefined), false);
  collection.preSave((docData, previous) => {
    setNextVersion(docData, previous);
  }, false);
}

function withoutAttachments(doc: unknown): unknown {
  const d = doc as { _attachments?: unknown };
  if (d?._attachments) return doc;
  return { ...(doc as object), _attachments: {} };
}

/**
 * The default handler with one extra rule: an incoming state older than the
 * one we hold is reported as equal, which makes the protocol leave the local
 * document alone.
 *
 * Only *strictly* older is ignored. Two devices can reach the same version
 * with different content — a genuine conflict — and that still falls through
 * to the default answer rather than being silently dropped.
 */
export function createVersionAwareConflictHandler<T>(): RxConflictHandler<T> {
  return {
    isEqual(a, b, context) {
      if (
        context === DOWNSTREAM_APPLY &&
        documentVersion(a) < documentVersion(b)
      ) {
        return true;
      }
      return deepEqual(withoutAttachments(a), withoutAttachments(b));
    },
    resolve: async (input) => input.realMasterState,
  };
}
